import api from "./api";

// Status = chave de coluna do kanban. Agora é dinâmico (colunas editáveis),
// por isso string em vez de união fixa.
export type TarefaStatus = string;

export type FeedbackTipo = "comentario" | "adiamento" | "conclusao" | "reabertura";
export type AnexoTipo = "imagem" | "audio" | "video" | "documento";
export type Recorrencia = "diaria" | "semanal" | "mensal";

export interface Anexo {
  id: string;
  feedbackId?: string | null;
  tipo: AnexoTipo;
  url: string;
  nomeOriginal: string;
  mimeType: string;
  tamanhoBytes: number;
  criadoPor: string;
  criadoEm: string;
  transcricao: string | null;
  transcricaoStatus: string | null;
}

export interface Subitem {
  id: string;
  texto: string;
  concluido: boolean;
  ordem: number;
  criadoPor?: string | null;
  autor?: { id: string; nome: string; email: string } | null;
}

export interface Feedback {
  id: string;
  tipo: FeedbackTipo;
  texto: string | null;
  novoPrazo: string | null;
  criadoEm: string;
  /** Preenchido quando o autor edita o comentário (Comentários #2). */
  editadoEm?: string | null;
  autor: { id: string; nome: string };
  anexos: Anexo[];
}

export interface Tarefa {
  id: string;
  projetoId: string | null;
  titulo: string;
  descricao: string | null;
  status: TarefaStatus;
  atribuidoA: string;
  /** O que a PRINCIPAL faz nesta tarefa (a dos co-responsáveis vai em `responsaveis[].funcao`). */
  funcaoPrincipal?: string | null;
  criadoPor: string | null;
  aprovadorId: string | null;
  tipoTarefaId?: string | null;
  linkDrive?: string | null;
  prazo: string;
  prazoOriginal: string | null;
  prazoComHora?: boolean;
  /** Data combinada com o CLIENTE (Fase 02, item 16). SÓ gestor/admin recebe do backend. */
  dataEntregaCliente?: string | null;
  concluidaEm: string | null;
  prioridade: string;
  etiquetas: string | null;
  ordemKanban?: number;
  recorrencia: Recorrencia | null;
  recorrenciaPaiId: string | null;
  arquivada?: boolean;
  arquivadaEm?: string | null;
  arquivadaPor?: string | null;
  solicArqPor?: string | null;
  solicArqMotivo?: string | null;
  solicArqEm?: string | null;
  visivelParaCliente?: boolean;
  /** "Esforço" estimado em horas decimais (1.5 = 1h30). null = sem estimativa. */
  horasEstimadas?: number | null;
  /** MELHORIA 15 — quantidade de peças entregues neste card (>=1). Soma na produtividade. */
  quantidadePecas?: number;
  criadoEm: string;
  atribuido?: { id: string; nome: string; email: string };
  criador?: { id: string; nome: string; email: string } | null;
  aprovador?: { id: string; nome: string; email: string } | null;
  projeto?: { id: string; nome: string; interno?: boolean; tipo?: string; fotoUrl?: string | null; clienteRef?: { id: string; nome: string; linkAssets: string | null } | null } | null;
  tipoTarefa?: { id: string; slug: string; label: string; cor: string | null; icone: string } | null;
  /** Co-responsáveis. `funcao` = o que a pessoa faz NESTA tarefa (catálogo FuncaoTarefa). */
  responsaveis?: Array<{ usuarioId: string; funcao?: string | null; usuario: { id: string; nome: string; email: string; fotoUrl: string | null } }>;
  anexos?: Anexo[];
  subitens?: Subitem[];
}

export interface TarefaDetalhe extends Tarefa {
  feedbacks: Feedback[];
  /** Tempo executado (soma das sessões úteis, horário comercial) em ms. CARDS 4. */
  tempoExecutadoMs?: number;
  trabalhandoAgora?: Array<{
    id: string;
    nome: string;
    fotoUrl: string | null;
    tarefaAtualDesde: string | null;
  }>;
}

export async function listarTarefas(filtros?: {
  projetoId?: string;
  atribuidoA?: string;
  status?: string;
  escopo?: "minhas";
  arquivadas?: boolean;
}) {
  const params = new URLSearchParams();
  if (filtros?.projetoId) params.append("projetoId", filtros.projetoId);
  if (filtros?.atribuidoA) params.append("atribuidoA", filtros.atribuidoA);
  if (filtros?.status) params.append("status", filtros.status);
  if (filtros?.escopo) params.append("escopo", filtros.escopo);
  if (filtros?.arquivadas) params.append("arquivadas", "true");
  const response = await api.get(`/tarefas?${params.toString()}`);
  return response.data.data as Tarefa[];
}

export async function buscarTarefa(id: string) {
  const response = await api.get(`/tarefas/${id}`);
  return response.data.data as TarefaDetalhe;
}

export async function criarTarefa(data: {
  projetoId?: string | null;
  titulo: string;
  descricao?: string;
  atribuidoA: string;
  responsaveis?: string[];
  tipoTarefaId?: string | null;
  linkDrive?: string | null;
  aprovadorId?: string | null;
  prazo: string;
  prazoComHora?: boolean;
  prioridade?: string;
  etiquetas?: string[];
  recorrencia?: Recorrencia | null;
  subitens?: string[];
  horasEstimadas?: number | null;
  quantidadePecas?: number;
}) {
  const response = await api.post("/tarefas", data);
  return response.data.data as Tarefa;
}

// Payload de update é permissivo: além dos campos de Tarefa, aceita `responsaveis`
// como lista de IDs (string[]) — diferente do shape de leitura (objetos).
export async function atualizarTarefa(id: string, data: Record<string, any>) {
  const response = await api.patch(`/tarefas/${id}`, data);
  return response.data.data as Tarefa;
}

export async function duplicarTarefa(id: string) {
  const r = await api.post(`/tarefas/${id}/duplicar`);
  return r.data.data as Tarefa;
}

export async function moverTarefa(id: string, status: TarefaStatus) {
  const response = await api.patch(`/tarefas/${id}/status`, { status });
  return response.data.data as Tarefa;
}

export async function deletarTarefa(id: string) {
  await api.delete(`/tarefas/${id}`);
}

export async function adicionarFeedback(
  id: string,
  payload: { tipo: FeedbackTipo; texto?: string; novoPrazo?: string }
) {
  const response = await api.post(`/tarefas/${id}/feedbacks`, payload);
  return response.data.data as Feedback;
}

export async function editarFeedback(tarefaId: string, feedbackId: string, texto: string) {
  const response = await api.patch(`/tarefas/${tarefaId}/feedbacks/${feedbackId}`, { texto });
  return response.data.data as Feedback;
}

export async function aprovarConclusao(id: string, aprovado: boolean, comentario?: string) {
  const response = await api.post(`/tarefas/${id}/aprovar`, { aprovado, comentario });
  return response.data.data as Tarefa;
}

// === Arquivamento ===
export async function arquivarTarefa(id: string, motivo?: string) {
  const r = await api.post(`/tarefas/${id}/arquivar`, { motivo });
  return r.data.data as { tarefa: Tarefa; foiSolicitacao: boolean };
}
export async function desarquivarTarefa(id: string) {
  const r = await api.post(`/tarefas/${id}/desarquivar`);
  return r.data.data as Tarefa;
}
export async function aprovarArquivamentoTarefa(id: string) {
  const r = await api.post(`/tarefas/${id}/aprovar-arquivamento`);
  return r.data.data as Tarefa;
}
export async function rejeitarArquivamentoTarefa(id: string, motivo?: string) {
  const r = await api.post(`/tarefas/${id}/rejeitar-arquivamento`, { motivo });
  return r.data.data as Tarefa;
}

export async function uploadAnexo(tarefaId: string, file: File | Blob, opts?: { nomeOriginal?: string; feedbackId?: string; ajusteId?: string }) {
  const form = new FormData();
  const fileName = opts?.nomeOriginal || (file as File).name || "anexo";
  form.append("file", file, fileName);
  const qs = new URLSearchParams();
  if (opts?.feedbackId) qs.append("feedbackId", opts.feedbackId);
  if (opts?.ajusteId) qs.append("ajusteId", opts.ajusteId);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const response = await api.post(`/tarefas/${tarefaId}/anexos${suffix}`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return response.data.data as Anexo;
}

export async function deletarAnexo(tarefaId: string, anexoId: string) {
  await api.delete(`/tarefas/${tarefaId}/anexos/${anexoId}`);
}

// Correção manual de tempo trabalhado (ledger auditável)
export interface TempoPessoa {
  usuario: { id: string; nome: string; fotoUrl: string | null };
  ms: number;
}
export type TempoAjusteStatus = "pendente" | "aprovado" | "rejeitado";
export interface TempoAjuste {
  id: string;
  tarefaId: string;
  usuarioId: string;
  deltaMs: number;
  totalAnteriorMs: number;
  totalNovoMs: number;
  motivo: string;
  criadoEm: string;
  /** pendente = aguardando gestor; só "aprovado" conta no tempo/relatórios. */
  status: TempoAjusteStatus;
  aprovadorId?: string | null;
  decididoEm?: string | null;
  decisaoMotivo?: string | null;
  usuario?: { id: string; nome: string; fotoUrl: string | null };
  autor?: { id: string; nome: string };
  aprovador?: { id: string; nome: string } | null;
  /** Só vem na lista de pendentes (contexto da tarefa). */
  tarefa?: { id: string; titulo: string; projeto: { id: string; nome: string } | null };
}
export async function listarTempoAjustes(tarefaId: string) {
  const r = await api.get(`/tarefas/${tarefaId}/tempo-ajustes`);
  return r.data.data as TempoAjuste[];
}
export async function ajustarTempo(tarefaId: string, dados: { usuarioId?: string; totalMinutos: number; motivo: string }) {
  const r = await api.post(`/tarefas/${tarefaId}/tempo-ajustes`, dados);
  return r.data.data as TempoAjuste;
}
// === Aprovação de ajustes de tempo (gestor/admin) ===
export async function listarTempoAjustesPendentes() {
  const r = await api.get(`/tarefas/tempo-ajustes/pendentes`);
  return r.data.data as TempoAjuste[];
}
export async function aprovarTempoAjuste(ajusteId: string) {
  const r = await api.post(`/tarefas/tempo-ajustes/${ajusteId}/aprovar`);
  return r.data.data as TempoAjuste;
}
export async function rejeitarTempoAjuste(ajusteId: string, motivo?: string) {
  const r = await api.post(`/tarefas/tempo-ajustes/${ajusteId}/rejeitar`, { motivo });
  return r.data.data as TempoAjuste;
}

// Atividade do card (histórico de auditoria da tarefa)
export interface EventoHistorico {
  id: string;
  autorNome: string | null;
  autorPerfil: string | null;
  acao: string;
  detalhes: any | null;
  criadoEm: string;
}
export async function listarHistoricoTarefa(tarefaId: string) {
  const r = await api.get(`/tarefas/${tarefaId}/historico`);
  return r.data.data as EventoHistorico[];
}

// Subitens
export async function criarSubitem(tarefaId: string, texto: string) {
  const response = await api.post(`/tarefas/${tarefaId}/subitens`, { texto });
  return response.data.data as Subitem;
}
export async function atualizarSubitem(tarefaId: string, subitemId: string, dados: Partial<Subitem>) {
  const response = await api.patch(`/tarefas/${tarefaId}/subitens/${subitemId}`, dados);
  return response.data.data as Subitem;
}
export async function deletarSubitem(tarefaId: string, subitemId: string) {
  await api.delete(`/tarefas/${tarefaId}/subitens/${subitemId}`);
}

// Helper: monta URL absoluta do anexo a partir da URL retornada pelo backend
export function anexoUrlAbsoluto(url: string): string {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  // api.baseURL termina com "/api"; precisamos da origem
  const apiBase = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${apiBase}${url}`;
}
